import { zodResolver } from "@hookform/resolvers/zod";
import { Button, IconButton } from "@hortis/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@hortis/ui/dialog";
import { XClose } from "@hortis/ui/icons";
import { toast } from "@hortis/ui/sonner";
import { useRouteContext } from "@tanstack/react-router";
import {
  useCreateTaxonMutation,
  useGetOrganisationFromNameQuery,
} from "generated/graphql";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { MissingValidationDialog } from "src/components/autocomplete/scientific-names/missing-validation-dialog";
import { TaxonScientificNameAutocomplete } from "src/components/autocomplete/scientific-names/taxon-scientific-name-autocomplete";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import {
  type BaseTaxonFields,
  baseTaxonFieldsSchema,
} from "src/features/taxonomy/components/new-taxon/schema";
import type { Nullable } from "src/types/nullable";
import { match } from "ts-pattern";

export const CreateTaxonDialog = ({
  onOpenChange,
  onSuccess: onSubmit,
  scientificName,
  open,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scientificName?: string | null;
  onSuccess: (newTaxon: { id: string; scientificName: string }) => void;
}) => {
  const { subdomain } = useRouteContext({ from: "__root__" });
  const [missingValidationDialogOpen, setMissingValidationDialogOpen] =
    useState(false);
  const [{ data: orgData }] = useGetOrganisationFromNameQuery({
    variables: { organisationSubdomain: subdomain },
  });
  const [, createTaxon] = useCreateTaxonMutation();
  const defaultValues = useMemo(
    () => ({
      scientificName: scientificName ?? null,
      sharedScientificName: null,
    }),
    [scientificName],
  );
  const formMethods = useForm<Nullable<BaseTaxonFields>>({
    defaultValues,
    criteriaMode: "all",
    resolver: zodResolver(baseTaxonFieldsSchema),
  });
  const sharedScientificName = formMethods.watch("sharedScientificName");

  const organisationId = orgData?.org?.id;

  useEffect(() => {
    if (open) {
      formMethods.reset(defaultValues);
      setMissingValidationDialogOpen(false);
    }
  }, [open, defaultValues, formMethods]);

  const handleSubmit = useMemo(
    () =>
      formMethods.handleSubmit(async (_data) => {
        if (sharedScientificName == null && !missingValidationDialogOpen) {
          setMissingValidationDialogOpen(true);
          return;
        }
        if (organisationId == null) {
          toast.error("Failed to create taxon, organization was not found");
          return;
        }
        try {
          const { sharedScientificName, ...data } = _data as BaseTaxonFields;
          const res = await createTaxon({
            input: {
              ...data,
              sharedScientificNameId: sharedScientificName?.id ?? null,
              organisationId,
            },
          });
          if (
            res.data?.createTaxon.success === true &&
            res.data.createTaxon.taxon != null
          ) {
            toast.success(`Successfully created taxon`);
            onOpenChange(false);
            onSubmit(res.data.createTaxon.taxon);
            return;
          }

          const errors = res.data?.createTaxon.errors;
          if (errors != null && errors.length > 0) {
            errors.forEach((error) => {
              match(error.__typename)
                .with("DuplicateScientificNameError", () => {
                  formMethods.setError("scientificName", {
                    message: error.message,
                  });
                })
                .exhaustive();
            });
            return;
          }
          throw new Error("Failed to create taxon");
        } catch {
          toast.error("Failed to create taxon");
        }
      }),
    [
      createTaxon,
      formMethods,
      onSubmit,
      organisationId,
      onOpenChange,
      sharedScientificName,
      missingValidationDialogOpen,
    ],
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <FormProvider {...formMethods}>
          <form className="flex flex-col gap-8" onSubmit={stopEventPropagation}>
            <DialogHeader className="space-y-5">
              <DialogTitle>New taxon</DialogTitle>
              <TaxonScientificNameAutocomplete
                name="scientificName"
                testId="new-taxon-scientific-name"
                focusOnMount
              />
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="tertiaryGray">Cancel</Button>
              </DialogClose>
              <MissingValidationDialog
                open={missingValidationDialogOpen}
                onOpenChange={setMissingValidationDialogOpen}
                onSubmit={handleSubmit}
              >
                <DialogTrigger asChild>
                  <Button
                    loading={formMethods.formState.isSubmitting}
                    variant="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      void handleSubmit();
                    }}
                    testId="new-taxon-submit"
                  >
                    Create taxon
                  </Button>
                </DialogTrigger>
              </MissingValidationDialog>
            </DialogFooter>
          </form>
        </FormProvider>
        <DialogClose asChild>
          <IconButton
            ariaLabel="Close"
            className="!absolute right-4! top-4!"
            size="xs"
            variant="tertiaryGray"
            testId="close-new-taxon-modal"
            icon={XClose}
          />
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
};
